import React, { useState, useEffect } from 'react';

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  className?: string;
  disabled?: boolean;
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscript, className = '', disabled = false }) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if speech recognition is supported
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = false;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onstart = () => {
        setIsListening(true);
        setError(null);
      };

      recognitionInstance.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        onTranscript(transcript);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
        console.log('Speech recognition ended');
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        switch (event.error) {
          case 'not-allowed':
            setPermissionStatus('denied');
            setError('Microphone access denied. Please enable microphone permissions in your browser settings.');
            break;
          case 'no-speech':
            setError('No speech detected. Please try again.');
            break;
          case 'audio-capture':
            setError('No microphone found. Please check your device settings.');
            break;
          case 'network':
            setError('Network error. Please check your internet connection.');
            break;
          case 'aborted':
            // Don't show error for user-initiated stops
            setError(null);
            break;
          case 'service-not-allowed':
            setError('Speech service not available. Please try again in a moment.');
            break;
          case 'bad-grammar':
            setError('Speech recognition service error. Please try again.');
            break;
          case 'language-not-supported':
            setError('Language not supported. Please check your browser settings.');
            break;
          default:
            setError(`Speech recognition error (${event.error}). Please try speaking more clearly or try again.`);
            console.log('Detailed error info:', {
              error: event.error,
              message: event.message,
              userAgent: navigator.userAgent,
              language: recognitionInstance.lang
            });
        }
      };

      setRecognition(recognitionInstance);
    }

    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, []);

  const requestMicrophonePermission = async () => {
    try {
      // Request microphone permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      setPermissionStatus('granted');
      setError(null);
      return true;
    } catch (error) {
      console.error('Microphone permission error:', error);
      setPermissionStatus('denied');
      setError('Microphone access is required for voice input. Please enable microphone permissions in your browser settings.');
      return false;
    }
  };

  const toggleListening = async () => {
    if (!recognition || disabled) return;

    if (isListening) {
      recognition.stop();
      return;
    }

    // Check for microphone permission first
    if (permissionStatus === 'unknown' || permissionStatus === 'prompt') {
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) return;
    }

    if (permissionStatus === 'denied') {
      setError('Microphone access denied. Please enable microphone permissions in your browser settings.');
      return;
    }

    try {
      setError(null);
      // Provide immediate visual feedback before starting recognition
      setIsListening(true);
      recognition.start();
    } catch (error) {
      console.error('Error starting recognition:', error);
      setError('Failed to start voice recognition. Please try again.');
      setIsListening(false);
    }
  };

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  return (
    <div className="relative hidden md:block">
      <button
        type="button"
        onClick={toggleListening}
        disabled={disabled}
        className={`p-2 rounded-full transition-colors ${
          isListening 
            ? 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 animate-pulse' 
            : 'bg-vellum text-ink/70 hover:bg-vellum hover:text-sun dark:bg-dusk dark:text-parchment/60 dark:hover:bg-shadow/30 dark:hover:text-ember'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        title={
          isListening 
            ? 'Listening... Tap to stop' 
            : permissionStatus === 'denied'
            ? 'Microphone access denied'
            : 'Tap to speak'
        }
      >
        <span className={`text-sm ${isListening ? 'animate-pulse' : ''}`}>{isListening ? '⏹' : '🎤'}</span>
      </button>
      
      {error && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-md shadow-lg border border-red-200 dark:border-red-800 z-50">
          <div className="flex items-start gap-2">
            <p className="text-xs">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInput;